import { Box, Breadcrumbs, Grid, Link, Paper, Typography } from "@material-ui/core";
import { Dashboard as DashboardIcon, Group, HowToReg, People, SupervisorAccount, Work as WorkIcon, Accessibility, PeopleAlt } from "@material-ui/icons";
import React, { useEffect, useRef, useState } from "react";
import CountUp from 'react-countup';
import PIS, { API_URL } from "../../../api/api";
import AddAlertMessage from "../../../components/alert/Alert";
import BarDiagram from "../../../components/charts/BarDiagram";
import DoughnutChart from "../../../components/charts/DoughnutChart";
import PieChart from "../../../components/charts/PieChart";
import { AppUtils } from "../../../utils/appUtils";
import { ERROR, SOMETHING_WENT_WRONG, SUCCESS } from "../../../utils/constants";
import { PALIKA } from "../../../utils/constants/forms";
import styles from "./style";

export default function Dashboard() {
  const classes = styles();
  const [currentView, setCurrentView] = useState('DEFAULT');
  const [genderReport, setGenderReport] = useState({});
  const [peopleRepresentativeCount, setPeopleRepresentativeCount] = useState(0);
  const [formerPeopleRepresentativeCount, setFormerPeopleRepresentativeCount] = useState(0);
  const [currentPeopleRepresentativeCount, setCurrentPeopleRepresentativeCount] = useState(0);
  const [ageGroupWiseReport, setAgeGroupWiseReport] = useState([]);
  const [employeeTypeReport, setEmployeeTypeReport] = useState([]);
  const [departmentLabels, setDepartmentLabels] = useState([]);
  const [departmentWiseReport, setDepartmentWiseReport] = useState([]);
  const [executiveAgeGroupReport, setExecutiveAgeGroupReport] = useState([]);
  const [executiveGenderReport, setExecutiveGenderReport] = useState({});
  const [todayAbsenteesReport, setTodayAbsenteesReport] = useState({});

  const [wardLabels, setWardLabels] = useState([]);
  const [wardWiseEmployeeCount, setWardWiseEmployeeCount] = useState([]);
  const [wardWiseGenderReport, setWardWiseGenderReport] = useState({});
  const [wardWisePostReport, setWardWisePostReport] = useState({});
  const [wardWiseCategoryReport, setWardWiseCategoryReport] = useState({});

  const officeType = AppUtils.getOfficeType();

  const isFetchedRef = useRef({
    DEFAULT: false,
    WARD: false,
    EXECUTIVE: false,
  });


  useEffect(() => {
    if (isFetchedRef.current[currentView]) return;
    isFetchedRef.current[currentView] = true;

    if (currentView === 'DEFAULT') {
      getGenderCount();
      getDepartmentWiseCount();
      getAgeGroupWiseStaffCount();
      getEmployeeTypeReport();
      getTodayAbsenteesCount();
    } else if (currentView === 'WARD') {
      getWardWiseStaff();
      getWardWiseGenderCount();
      getWardWisePostCount();
      getWardWiseCategoryCount();
    } else if (currentView === 'EXECUTIVE') {
      getPeopleRepresentativeCount();
      getExecutiveAgeGroupCount();
      getExecutiveGenderCount();
    }
  }, [currentView]);

  const getGenderCount = async () => {
    await PIS.get(`${API_URL.public}?method=GENDER_COUNT`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          setGenderReport(jsondata.data || {});
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      })
  }

  const getPeopleRepresentativeCount = async () => {
    await PIS.get(`${API_URL.public}?method=PEOPLE_REPRESENTATIVE_COUNT`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          if (typeof jsondata.data === 'object') {
            setPeopleRepresentativeCount(jsondata.data.total || 0);
            setFormerPeopleRepresentativeCount(jsondata.data.former || 0);
            setCurrentPeopleRepresentativeCount(jsondata.data.current || 0);
          } else {
            setPeopleRepresentativeCount(jsondata.data || 0);
          }
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      })
  }

  const getDepartmentWiseCount = async () => {
    await PIS.get(`${API_URL.public}?method=DEPARTMENT_WISE_COUNT`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          setDepartmentLabels(Object.keys(jsondata.data));
          setDepartmentWiseReport(Object.values(jsondata.data));
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      })
  }

  const getExecutiveAgeGroupCount = async () => {
    await PIS.get(`${API_URL.public}?method=ALL_PEOPLE_REPRESENTATIVE_BY_AGE_GROUP`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          let report = jsondata.data;
          setExecutiveAgeGroupReport([
            report.ageGroupBetweenFifteenToTwentyFour || 0,
            report.ageGroupBetweenTwentyFiveToThirtyFour || 0,
            report.ageGroupBetweenThirtyFiveToFortyFour || 0,
            report.ageGroupBetweenFortyFiveToFiftyFour || 0,
            report.ageGroupBetweenFiftyFiveToSixtyFour || 0,
            report.ageGroupAboveSixtyFive || 0
          ]);
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  }

  const getExecutiveGenderCount = async () => {
    await PIS.get(`${API_URL.public}?method=ALL_PEOPLE_REPRESENTATIVE_BY_GENDER`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          const data = jsondata.data || {};
          setExecutiveGenderReport({
            maleEmployee: data.maleEmployee || 0,
            femaleEmployee: data.femaleEmployee || 0,
            otherEmployee: data.otherEmployee || 0,
            totalEmployee: data.totalEmployee || 0
          });
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  }

  const getAgeGroupWiseStaffCount = async () => {
    await PIS.get(`${API_URL.public}?method=AGE_GROUP`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          let report = jsondata.data;
          setAgeGroupWiseReport([report.ageGroupBetweenFifteenToTwentyFour, report.ageGroupBetweenTwentyFiveToThirtyFour, report.ageGroupBetweenThirtyFiveToFortyFour, report.ageGroupBetweenFortyFiveToFiftyFour, report.ageGroupBetweenFiftyFiveToSixtyFour, report.ageGroupAboveSixtyFive]);
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      })
  }

  const getEmployeeTypeReport = async () => {
    await PIS.get(`${API_URL.public}?method=EMPLOYEE_TYPE_COUNT`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          let report = jsondata.data;
          setEmployeeTypeReport([report.permanent, report.temporary, report.karar, report.anya]);
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      })
  }

  const getTodayAbsenteesCount = async () => {
    await PIS.get(`${API_URL.public}?method=TODAY_ABSENTEES_BY_GENDER`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          setTodayAbsenteesReport(jsondata.data || {});
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  }

  const getWardWiseStaff = async () => {
    await PIS.get(`${API_URL.public}?method=WARD_WISE_COUNT`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          const validData = {};
          Object.keys(jsondata.data).forEach(key => {
            if (key) {
              validData[key] = jsondata.data[key];
            }
          });
          setWardLabels(Object.keys(validData));
          setWardWiseEmployeeCount(Object.values(validData));
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      })
  }

  const getWardWiseGenderCount = async () => {
    await PIS.get(`${API_URL.public}?method=WARD_WISE_GENDER_COUNT`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          const validData = {};
          Object.keys(jsondata.data).forEach(key => {
            if (key) {
              validData[key] = jsondata.data[key];
            }
          });
          setWardWiseGenderReport(validData);
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  }

  const getWardWisePostCount = async () => {
    await PIS.get(`${API_URL.public}?method=WARD_WISE_POST_COUNT`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          setWardWisePostReport(jsondata.data || {});
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  }

  const getWardWiseCategoryCount = async () => {
    await PIS.get(`${API_URL.public}?method=WARD_WISE_CATEGORY_COUNT`)
      .then(response => {
        let jsondata = response.data;
        if (jsondata.type === SUCCESS) {
          setWardWiseCategoryReport(jsondata.data || {});
        } else {
          AddAlertMessage({ type: jsondata.type, message: jsondata.message });
        }
      }).catch(error => {
        AddAlertMessage({ type: ERROR, message: SOMETHING_WENT_WRONG });
      });
  }

  const getCounts = (count, label = "जना") => {
    return (
      <Box display="flex" alignItems="baseline">
        <Typography className={classes.cardValue}>
          <CountUp delay={0.5} end={count || 0} duration={2} />
        </Typography>
        <Typography className={classes.unitText}> {label}</Typography>
      </Box>
    )
  }

  const renderBreadcrumbs = () => {
    return (
      <Box className={classes.breadcrumbContainer}>
        <Breadcrumbs separator="›" aria-label="breadcrumb">
          <Box
            className={`${classes.breadcrumbLink} ${currentView === 'DEFAULT' ? classes.activeBreadcrumb : ''}`}
            onClick={() => setCurrentView('DEFAULT')}
          >
            <DashboardIcon /> कर्मचारी
          </Box>
          <Box
            className={`${classes.breadcrumbLink} ${currentView === 'WARD' ? classes.activeBreadcrumb : ''}`}
            onClick={() => setCurrentView('WARD')}
          >
            <WorkIcon /> वडा कर्मचारी
          </Box>
          {officeType === PALIKA && (
            <Box
              className={`${classes.breadcrumbLink} ${currentView === 'EXECUTIVE' ? classes.activeBreadcrumb : ''}`}
              onClick={() => setCurrentView('EXECUTIVE')}
            >
              <SupervisorAccount /> कार्यपालिका सदस्य
            </Box>
          )}
        </Breadcrumbs>
      </Box>
    );
  };

  const renderDefaultView = () => (
    <>
      <Box display="flex" flexWrap="wrap" mx={-1.5} mb={3}>
        <Box p={1.5} width={{ xs: '100%', sm: '50%', md: '33.333%', lg: '20%' }}>
          <Link href='/user/staffs?filter=MALE' className={classes.card}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box className={`${classes.cardIconContainer} ${classes.blueIconBg}`}>
                <Accessibility style={{ fontSize: '1.25rem' }} />
              </Box>
              <Typography className={`${classes.badge} ${classes.blueBadge}`}>MALE</Typography>
            </Box>
            <Box className={classes.cardContentBox}>
              <Typography className={classes.cardTitle}>पुरुष कर्मचारी</Typography>
              {getCounts(genderReport.maleEmployee)}
            </Box>
          </Link>
        </Box>

        <Box p={1.5} width={{ xs: '100%', sm: '50%', md: '33.333%', lg: '20%' }}>
          <Link href='/user/staffs?filter=FEMALE' className={classes.card}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box className={`${classes.cardIconContainer} ${classes.blueIconBg}`}>
                <PeopleAlt style={{ fontSize: '1.25rem' }} />
              </Box>
              <Typography className={`${classes.badge} ${classes.blueBadge}`}>FEMALE</Typography>
            </Box>
            <Box className={classes.cardContentBox}>
              <Typography className={classes.cardTitle}>महिला कर्मचारी</Typography>
              {getCounts(genderReport.femaleEmployee)}
            </Box>
          </Link>
        </Box>

        <Box p={1.5} width={{ xs: '100%', sm: '50%', md: '33.333%', lg: '20%' }}>
          <Link href='/user/staffs' className={classes.card}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box className={`${classes.cardIconContainer} ${classes.blueDarkIconBg}`}>
                <People style={{ fontSize: '1.25rem' }} />
              </Box>
              <Box className={`${classes.badge} ${classes.grayBadge}`}>
                {genderReport.totalEmployee}
              </Box>
            </Box>
            <Box className={classes.cardContentBox}>
              <Typography className={classes.cardTitle}>जम्मा कर्मचारी</Typography>
              {getCounts(genderReport.totalEmployee, "कुल")}
            </Box>
          </Link>
        </Box>

        <Box p={1.5} width={{ xs: '100%', sm: '50%', md: '33.333%', lg: '20%' }}>
          <Link href='/user/staffs?formerEmployee=false' className={classes.card}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box className={`${classes.cardIconContainer} ${classes.blueIconBg}`}>
                <HowToReg style={{ fontSize: '1.25rem' }} />
              </Box>
              <Typography className={classes.badge} style={{ color: '#2563eb', tracking: 2, textTransform: 'uppercase' }}>Active</Typography>
            </Box>
            <Box className={classes.cardContentBox}>
              <Typography className={classes.cardTitle}>कार्यरत कर्मचारी</Typography>
              {getCounts(genderReport.currentStaff)}
            </Box>
          </Link>
        </Box>

        <Box p={1.5} width={{ xs: '100%', sm: '50%', md: '33.333%', lg: '20%' }}>
          <Link href='/user/staffs?formerEmployee=true' className={classes.card}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box className={`${classes.cardIconContainer} ${classes.blueIconBg}`}>
                <HowToReg style={{ fontSize: '1.25rem' }} />
              </Box>
              <Typography className={classes.badge} style={{ color: '#6b7280', tracking: 2, textTransform: 'uppercase' }}>Former</Typography>
            </Box>
            <Box className={classes.cardContentBox}>
              <Typography className={classes.cardTitle}>भूतपूर्व कर्मचारी</Typography>
              {getCounts(genderReport.formerStaff)}
            </Box>
          </Link>
        </Box>
      </Box>

      <Grid container spacing={3} style={{ marginBottom: 24 }}>
        {employeeTypeReport?.some(val => val > 0) && (
          <Grid item xs={12} lg={4}>
            <Paper className={classes.card} elevation={0}>
              <Box className={classes.cardIconBox}>
                <Box>
                  <Typography className={classes.sectionTitle}>कर्मचारी प्रकार</Typography>
                  <Typography className={classes.sectionSubtitle}>कर्मचारी वर्गीकरण</Typography>
                </Box>
              </Box>
              <Box className={classes.chartContainer}>
                <DoughnutChart
                  chartData={employeeTypeReport}
                  labels={['स्थाई', 'अस्थायी', 'करार', 'अन्य']}
                  backgroundColor={['#1e40af', '#3b82f6', '#93c5fd', '#dbeafe']}
                  options={{
                    cutout: '75%',
                    plugins: { legend: { display: false } },
                    maintainAspectRatio: false
                  }}
                />
              </Box>
              <Grid container spacing={2} className={classes.legendGrid}>
                {[
                  { label: 'स्थाई', color: '#2563eb', val: employeeTypeReport[0] },
                  { label: 'अस्थायी', color: '#60a5fa', val: employeeTypeReport[1] },
                  { label: 'करार', color: '#93c5fd', val: employeeTypeReport[2] },
                  { label: 'अन्य', color: '#eff6ff', val: employeeTypeReport[3] }
                ].map((item, idx) => (
                  <Grid item xs={6} key={idx}>
                    <Box className={classes.legendItem}>
                      <Box display="flex" alignItems="center" mb={0.5}>
                        <Box className={classes.legendDot} style={{ backgroundColor: item.color }} />
                        <Typography className={classes.legendLabel}>{item.label}</Typography>
                      </Box>
                      <Typography className={classes.legendValue}>{item.val || 0} जना</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
        )}

        {ageGroupWiseReport?.some(val => val > 0) && (
          <Grid item xs={12} lg={employeeTypeReport?.some(val => val > 0) ? 8 : 12}>
            <Paper className={classes.card} elevation={0}>
              <Box className={classes.cardIconBox}>
                <Box>
                  <Typography className={classes.sectionTitle}>उमेर समूह</Typography>
                  <Typography className={classes.sectionSubtitle}>कर्मचारी विवरण</Typography>
                </Box>
              </Box>
              <Box className={classes.chartContainer} style={{ minHeight: 350 }}>
                <BarDiagram
                  data={ageGroupWiseReport}
                  labels={['15-24', '25-34', '35-44', '45-54', '55-64', '65+']}
                  diagramLabel="कर्मचारी संख्या"
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      yAxes: [{
                        ticks: {
                          beginAtZero: true,
                          fontSize: 10,
                          fontColor: '#9ca3af'
                        },
                        gridLines: { color: '#f3f4f6' }
                      }],
                      xAxes: [{
                        gridLines: { display: false },
                        ticks: {
                          fontSize: 10,
                          fontColor: '#6b7280'
                        }
                      }]
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </>
  );

  const renderWardView = () => {
    const isWardDataEmpty = !wardWiseEmployeeCount?.some(val => val > 0) &&
      Object.keys(wardWiseGenderReport).length === 0 &&
      Object.keys(wardWisePostReport).length === 0 &&
      Object.keys(wardWiseCategoryReport).length === 0;

    if (isWardDataEmpty) {
      return (
        <Box className={classes.emptyStateContainer}>
          <Typography variant="h6" color="textSecondary" align="center">
            माफ गर्नुहोस्, कुनै रेकर्ड फेला परेन।
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {wardWiseEmployeeCount?.some(val => val > 0) && (
          <Grid item xs={12} lg={6}>
            <Paper className={classes.card} elevation={0}>
              <Box className={classes.cardIconBox}>
                <Box>
                  <Typography className={classes.sectionTitle}>जम्मा कर्मचारी</Typography>
                  <Typography className={classes.sectionSubtitle}>वडामा कार्यरत कर्मचारी</Typography>
                </Box>
              </Box>
              <Box className={`${classes.chartContainer} ${classes.doughnutChartContainer}`}>
                <DoughnutChart
                  chartData={wardWiseEmployeeCount}
                  labels={wardLabels.map(l => l === "00" ? "केन्द्र" : `वडा ${l}`)}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom', display: false } }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}
        {Object.keys(wardWiseGenderReport).length > 0 && (
          <Grid item xs={12} lg={wardWiseEmployeeCount?.some(val => val > 0) ? 6 : 12}>
            <Paper className={classes.card} elevation={0}>
              <Box className={classes.cardIconBox}>
                <Box>
                  <Typography className={classes.sectionTitle}>लैंगिक विवरण</Typography>
                  <Typography className={classes.sectionSubtitle}>वडामा कार्यरत कर्मचारी</Typography>
                </Box>
              </Box>
              <Box className={`${classes.chartContainer} ${classes.chartContainerLarge}`}>
                <BarDiagram
                  labels={Object.keys(wardWiseGenderReport).map(l => l === "00" ? "केन्द्र" : `वडा ${l}`)}
                  datasets={[
                    {
                      label: 'पुरुष',
                      data: Object.values(wardWiseGenderReport).map(d => d.maleEmployee || 0),
                      backgroundColor: '#1e40af',
                    },
                    {
                      label: 'महिला',
                      data: Object.values(wardWiseGenderReport).map(d => d.femaleEmployee || 0),
                      backgroundColor: '#db2777',
                    }
                  ]}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'top' } },
                    scales: {
                      yAxes: [{
                        ticks: {
                          beginAtZero: true,
                          fontSize: 10,
                          fontColor: '#9ca3af',
                          stepSize: 1
                        },
                        gridLines: { color: '#f3f4f6' }
                      }],
                      xAxes: [{
                        gridLines: { display: false },
                        ticks: {
                          fontSize: 10,
                          fontColor: '#6b7280'
                        }
                      }]
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}
        {Object.keys(wardWisePostReport).length > 0 && (
          <Grid item xs={12} lg={6}>
            <Paper className={classes.card} elevation={0}>
              <Box className={classes.cardIconBox}>
                <Box>
                  <Typography className={classes.sectionTitle}>पद अनुसार कर्मचारी विवरण</Typography>
                  <Typography className={classes.sectionSubtitle}>पदगत विवरण</Typography>
                </Box>
              </Box>
              <Box className={`${classes.chartContainer} ${classes.chartContainerLarge}`}>
                <BarDiagram
                  data={Object.values(wardWisePostReport)}
                  labels={Object.keys(wardWisePostReport)}
                  diagramLabel="कर्मचारी संख्या"
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      yAxes: [{
                        ticks: { beginAtZero: true, fontSize: 10, fontColor: '#9ca3af', stepSize: 1 },
                        gridLines: { color: '#f3f4f6' }
                      }],
                      xAxes: [{
                        gridLines: { display: false },
                        ticks: { fontSize: 10, fontColor: '#6b7280' }
                      }]
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}
        {Object.keys(wardWiseCategoryReport).length > 0 && (
          <Grid item xs={12} lg={Object.keys(wardWisePostReport).length > 0 ? 6 : 12}>
            <Paper className={classes.card} elevation={0}>
              <Box className={classes.cardIconBox}>
                <Box>
                  <Typography className={classes.sectionTitle}>श्रेणी अनुसार कर्मचारी विवरण</Typography>
                  <Typography className={classes.sectionSubtitle}>श्रेणीगत विवरण</Typography>
                </Box>
              </Box>
              <Box className={`${classes.chartContainer} ${classes.chartContainerLarge}`}>
                <PieChart
                  chartData={Object.values(wardWiseCategoryReport)}
                  labels={Object.keys(wardWiseCategoryReport)}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom' } }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderExecutiveView = () => {
    const isExecutiveDataEmpty = peopleRepresentativeCount === 0 &&
      currentPeopleRepresentativeCount === 0 &&
      formerPeopleRepresentativeCount === 0 &&
      executiveGenderReport.totalEmployee === 0 &&
      !executiveAgeGroupReport?.some(val => val > 0);

    if (isExecutiveDataEmpty) {
      return (
        <Box className={classes.emptyStateContainer}>
          <Typography variant="h6" color="textSecondary" align="center">
            माफ गर्नुहोस्, कुनै रेकर्ड फेला परेन।
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={3}>
        {peopleRepresentativeCount > 0 && (
          <Grid item xs={12} md={4}>
            <Link href='/user/public-representatives' className={classes.card}>
              <Box className={`${classes.cardIconContainer} ${classes.blueDarkIconBg}`} mb={2}>
                <SupervisorAccount style={{ fontSize: '1.5rem' }} />
              </Box>
              <Typography className={classes.cardTitle}>जम्मा जन प्रतिनिधि</Typography>
              {getCounts(peopleRepresentativeCount)}
            </Link>
          </Grid>
        )}
        {currentPeopleRepresentativeCount > 0 && (
          <Grid item xs={12} md={4}>
            <Link href='/user/public-representatives?formerPublicRepresentative=false' className={classes.card}>
              <Box className={`${classes.cardIconContainer} ${classes.blueIconBg}`} mb={2}>
                <Group style={{ fontSize: '1.5rem' }} />
              </Box>
              <Typography className={classes.cardTitle}>कार्यरत जन प्रतिनिधि</Typography>
              {getCounts(currentPeopleRepresentativeCount)}
            </Link>
          </Grid>
        )}
        {formerPeopleRepresentativeCount > 0 && (
          <Grid item xs={12} md={4}>
            <Link href='/user/public-representatives?formerPublicRepresentative=true' className={classes.card}>
              <Box className={`${classes.cardIconContainer} ${classes.blueIconBg}`} mb={2}>
                <HowToReg style={{ fontSize: '1.5rem' }} />
              </Box>
              <Typography className={classes.cardTitle}>भूतपूर्व जन प्रतिनिधि</Typography>
              {getCounts(formerPeopleRepresentativeCount)}
            </Link>
          </Grid>
        )}

        {executiveGenderReport.totalEmployee > 0 && (
          <Grid item xs={12} lg={4}>
            <Paper className={classes.card} elevation={0}>
              <Box className={classes.cardIconBox}>
                <Box>
                  <Typography className={classes.sectionTitle}>लैंगिक विवरण</Typography>
                  <Typography className={classes.sectionSubtitle}>जन प्रतिनिधि विवरण</Typography>
                </Box>
              </Box>
              <Box className={`${classes.chartContainer} ${classes.chartContainerLarge}`}>
                <PieChart
                  chartData={[executiveGenderReport.maleEmployee || 0, executiveGenderReport.femaleEmployee || 0, executiveGenderReport.otherEmployee || 0]}
                  labels={['पुरुष', 'महिला', 'अन्य']}
                  backgroundColor={['#1e40af', '#db2777', '#93c5fd']}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { position: 'bottom' } }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}
        {executiveAgeGroupReport?.some(val => val > 0) && (
          <Grid item xs={12} lg={executiveGenderReport.totalEmployee > 0 ? 8 : 12}>
            <Paper className={classes.card} elevation={0}>
              <Box className={classes.cardIconBox}>
                <Box>
                  <Typography className={classes.sectionTitle}>उमेर समूह</Typography>
                  <Typography className={classes.sectionSubtitle}>जन प्रतिनिधि विवरण</Typography>
                </Box>
              </Box>
              <Box className={`${classes.chartContainer} ${classes.chartContainerLarge}`}>
                <BarDiagram
                  data={executiveAgeGroupReport}
                  labels={['15-24', '25-34', '35-44', '45-54', '55-64', '65+']}
                  diagramLabel="जम्मा संख्या"
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: {
                      yAxes: [{
                        ticks: {
                          beginAtZero: true,
                          fontSize: 10,
                          fontColor: '#9ca3af'
                        },
                        gridLines: { color: '#f3f4f6' }
                      }],
                      xAxes: [{
                        gridLines: { display: false },
                        ticks: {
                          fontSize: 10,
                          fontColor: '#6b7280'
                        }
                      }]
                    }
                  }}
                />
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Box className={classes.dashboardWrapper}>
      {renderBreadcrumbs()}

      {currentView === 'DEFAULT' && renderDefaultView()}
      {currentView === 'WARD' && renderWardView()}
      {currentView === 'EXECUTIVE' && renderExecutiveView()}

      {currentView === 'DEFAULT' && (
        <Grid container spacing={3} mt={3}>
          {departmentWiseReport?.some(val => val > 0) && (
            <Grid item xs={12} lg={todayAbsenteesReport?.totalEmployee > 0 ? 6 : 8}>
              <Paper className={classes.card} elevation={0}>
                <Box className={classes.cardIconBox}>
                  <Box>
                    <Typography className={classes.sectionTitle}>विभाग／शाखा</Typography>
                    <Typography className={classes.sectionSubtitle}>कर्मचारी विवरण</Typography>
                  </Box>
                </Box>
                <Grid container alignItems="center" spacing={4}>
                  <Grid item xs={12} md={5}>
                    <Box className={classes.chartContainer} style={{ minHeight: 250 }}>
                      <PieChart
                        chartData={departmentWiseReport}
                        labels={departmentLabels}
                        backgroundColor={['#1d4ed8', '#3b82f6', '#93c5fd', '#dbeafe', '#60a5fa', '#2563eb', '#1e40af']}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={7}>
                    <Grid container spacing={2}>
                      {departmentLabels.slice(0, 4).map((label, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <Box className={classes.deptItem}>
                            <Box className={classes.deptDot} style={{ backgroundColor: ['#1d4ed8', '#3b82f6', '#93c5fd', '#dbeafe'][index] }} />
                            <Box>
                              <Typography className={classes.legendLabel}>{label}</Typography>
                              {getCounts(departmentWiseReport[index])}
                            </Box>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          {todayAbsenteesReport?.totalEmployee > 0 && (
            <Grid item xs={12} lg={departmentWiseReport?.some(val => val > 0) ? 6 : 8}>
              <Paper className={classes.card} elevation={0}>
                <Box className={classes.cardIconBox}>
                  <Box>
                    <Typography className={classes.sectionTitle}>आज अनुपस्थित कर्मचारी</Typography>
                    <Typography className={classes.sectionSubtitle}>लिङ्ग अनुसार विवरण</Typography>
                  </Box>
                </Box>
                <Box className={`${classes.chartContainer} ${classes.chartContainerMedium}`}>
                  <DoughnutChart
                    chartData={[todayAbsenteesReport.maleEmployee || 0, todayAbsenteesReport.femaleEmployee || 0, todayAbsenteesReport.otherEmployee || 0]}
                    labels={['पुरुष', 'महिला', 'अन्य']}
                    backgroundColor={['#1e40af', '#db2777', '#93c5fd']}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '70%',
                      plugins: { legend: { display: false } }
                    }}
                  />
                </Box>
                <Grid container spacing={2} className={classes.legendGrid} mt={2}>
                  {[
                    { label: 'पुरुष', color: '#1e40af', val: todayAbsenteesReport.maleEmployee },
                    { label: 'महिला', color: '#db2777', val: todayAbsenteesReport.femaleEmployee },
                    { label: 'अन्य', color: '#93c5fd', val: todayAbsenteesReport.otherEmployee }
                  ].map((item, idx) => (
                    <Grid item xs={4} key={idx}>
                      <Box className={classes.legendItem}>
                        <Box display="flex" alignItems="center" mb={0.5}>
                          <Box className={classes.legendDot} style={{ backgroundColor: item.color }} />
                          <Typography className={classes.legendLabel}>{item.label}</Typography>
                        </Box>
                        <Typography className={classes.legendValue}>{item.val || 0} जना</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
}
